import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  useWonEngagements,
  useCreateEngagement,
  useUpdateEngagement,
  useSyncActuals,
} from '../../hooks/engagement/useEngagement';
import apiService from '../../services/api';
import { INDUSTRIES } from '../../components/bids/constants/industries';

import { componentsToServices, buildServicesFromBaseline } from '../../components/bids/utils/bidWizardHelpers';

// Components
import { getEmptyFormData, engagementToFormData, formDataToPayload } from '../../components/bids/engagements/EngagementModal';
import WizardStepper          from '../../components/bids/wizard/WizardStepper';
import WizardNavigation       from '../../components/bids/wizard/WizardNavigation';
import Step1EngagementType    from '../../components/bids/wizard/steps/Step1EngagementType';
import Step2ReferenceDecision from '../../components/bids/wizard/steps/Step2ReferenceDecision';
import Step3SelectTemplate    from '../../components/bids/wizard/steps/Step3SelectTemplate';
import Step3BaselineSelection from '../../components/bids/wizard/steps/Step3BaselineSelection';
import Step4BuildServices     from '../../components/bids/wizard/steps/Step4BuildServices';
import Step5GenerateProposal  from '../../components/bids/wizard/steps/Step5GenerateProposal';
import PreviewModal           from '../../components/bids/documents/PreviewModal';
import EngagementModal        from '../../components/bids/engagements/EngagementModal';

// Hooks
import { useBidEngagements, useBidEngagementComponents } from '../../hooks/bid_wizard/useBidEngagements';
import { useBidWizardState }   from '../../hooks/bid_wizard/useBidWizardState';
import { useServiceManagement } from '../../hooks/bid_wizard/useServiceManagement';

const BidWizard = ({ onSaveRequest, isSaving, initialWizardData, initialStep, initialMargin: initialMarginProp, initialRateOverrides: initialRateOverridesProp }) => {
  // ── Custom hooks ──────────────────────────────────────────────────
  const {
    currentStep,
    wizardData,
    setWizardData,
    calculations,
    pricingContext,
    clientIntelligence,
    insights,
    goToStep,
    nextStep,
    previousStep,
    canProceed,
    selectEngagementType,
    selectReferenceDecision,
    selectEngagementTemplate,
    applyActualsToServices,
    updateFilter,
  } = useBidWizardState(initialWizardData ?? null, initialStep ?? 1);

  const serviceManagement = useServiceManagement(wizardData, setWizardData);

  const [preFilteredComponents, setPreFilteredComponents] = useState(null);

  // ── Derived routing values ────────────────────────────────────────
  const referenceDecision = wizardData.useReference;

  const totalSteps  = referenceDecision === 'scratch' ? 4 : 5;
  const displayStep = (referenceDecision === 'scratch' && currentStep >= 4)
    ? currentStep - 1
    : currentStep;

  // ── Loaded scenario state ─────────────────────────────────────────
  const [loadedMargin,        setLoadedMargin]        = useState(initialMarginProp);
  const [loadedRateOverrides, setLoadedRateOverrides] = useState(initialRateOverridesProp);
  const [loadedClientName,    setLoadedClientName]    = useState(undefined);
  const [loadedNotes,         setLoadedNotes]         = useState(undefined);
  const [loadedScenarioId,    setLoadedScenarioId]    = useState(undefined);
  const [loadedScenarioName,  setLoadedScenarioName]  = useState(undefined);

  // ── Per-row save state for inline indicators ──────────────────────
  // { [engagementId]: 'saving' | 'saved' | 'error' }
  const [saveStates, setSaveStates] = useState({});

  // ── Auto-sync actuals state ─────────────────────────────────────
  const [autoSyncing,   setAutoSyncing]   = useState(false);
  const [autoSyncError, setAutoSyncError] = useState(null);
  const autoSyncAttemptedRef = useRef(null);

  // ── Components for selected template (Path A) ─────────────────────
  const selectedTemplateId = wizardData.selectedTemplate?.id || null;
  const isRetainer         = wizardData.engagementType === 'retainer';

  const {
    data:      templateComponents,
    isLoading: componentsLoading,
  } = useBidEngagementComponents({
    engagementId: selectedTemplateId,
    enabled:      !!selectedTemplateId,
  });

  useEffect(() => {
    if (preFilteredComponents?.length && selectedTemplateId) {
      const engagementName =
        wizardData.selectedTemplate?.engagement_name ||
        wizardData.selectedTemplate?.clientName      ||
        wizardData.componentName                     ||
        'Services';
      const services = componentsToServices(preFilteredComponents, engagementName, isRetainer);
      if (services) applyActualsToServices(services);
      return;
    }

    if (componentsLoading || !templateComponents?.components?.length || !selectedTemplateId) return;

    const engagementName =
      wizardData.selectedTemplate?.engagement_name ||
      wizardData.selectedTemplate?.clientName      ||
      wizardData.componentName                     ||
      'Services';

    const services = componentsToServices(templateComponents.components, engagementName, isRetainer);
    if (services) applyActualsToServices(services);
  }, [templateComponents, componentsLoading, selectedTemplateId, isRetainer, preFilteredComponents]);

  // ── Auto-sync actuals when template has no data ─────────────────
  const syncActualsMutation = useSyncActuals();

  useEffect(() => {
    if (!selectedTemplateId) return;
    if (componentsLoading) return;
    if (templateComponents?.components?.length) return;          // already has data
    if (preFilteredComponents?.length) return;                   // came from modal filter
    if (!wizardData.selectedTemplate?.qb_customer_ref) return;   // no QB link
    if (autoSyncAttemptedRef.current === selectedTemplateId) return; // already tried

    autoSyncAttemptedRef.current = selectedTemplateId;
    setAutoSyncing(true);
    setAutoSyncError(null);

    syncActualsMutation.mutateAsync(selectedTemplateId)
      .then(() => {
        setAutoSyncing(false);
        // Cache invalidation in useSyncActuals.onSuccess auto-refetches components
      })
      .catch((err) => {
        setAutoSyncing(false);
        setAutoSyncError(err?.message || 'Failed to sync actuals from QuickBooks.');
      });
  }, [selectedTemplateId, componentsLoading, templateComponents, preFilteredComponents, wizardData.selectedTemplate?.qb_customer_ref]);

  // ── Engagement modal state ────────────────────────────────────────
  const [showEngagementModal, setShowEngagementModal] = useState(false);
  const [editingEngagement,   setEditingEngagement]   = useState(null);
  const [engagementFormData,  setEngagementFormData]  = useState(getEmptyFormData());
  const originalDocIdRef = React.useRef(null);

  // ── Preview modal state ───────────────────────────────────────────
  const [showPreviewModal,  setShowPreviewModal]  = useState(false);
  const [previewDocumentId, setPreviewDocumentId] = useState(null);

  // ── Document state ────────────────────────────────────────────────
  const [documents,        setDocuments]        = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  useEffect(() => {
    if (showEngagementModal) {
      (async () => {
        setLoadingDocuments(true);
        try {
          const response = await apiService.get('/documents', { limit: 100 });
          setDocuments(response?.documents || []);
        } catch (err) {
          console.error('Error fetching documents:', err);
        } finally {
          setLoadingDocuments(false);
        }
      })();
    }
  }, [showEngagementModal]);

  // ── Fetch Won engagements ─────────────────────────────────────────
  const {
    data:      wonEngagementsData,
    isLoading: isLoadingEngagements,
    refetch:   refetchEngagements,
  } = useWonEngagements({
    engagement_type:
      wizardData.engagementType === 'retainer' ? 'Retainer' :
      wizardData.engagementType === 'project'  ? 'Project'  : undefined,
    limit:   500,
    enabled: currentStep >= 2,
  });

  const createEngagementMutation = useCreateEngagement();
  const updateEngagementMutation = useUpdateEngagement();

  const realEngagements = useMemo(() => {
    const list = wonEngagementsData?.engagements || [];
    if (!list.length) return list;

    const hash = (s) => {
      let h = 0;
      const str = String(s);
      for (let i = 0; i < str.length; i++) {
        h = ((h << 5) - h) + str.charCodeAt(i);
        h |= 0;
      }
      return Math.abs(h);
    };

    return list.map((eng) => {
      const seed = eng.id ?? eng.engagement_name ?? '';
      const isRetainer = eng.engagement_type === 'Retainer';

      // Bid amount: 50K – 5M, snapped to nearest 10K
      const bidRange = 5_000_000 - 50_000;
      const mockBid =
        50_000 + Math.round(((hash(seed + ':bid')) % bidRange) / 10_000) * 10_000;

      // Revenue to date: 70%–130% of bid, clamped to 50K–5M
      const variance = 0.7 + (hash(seed + ':rev') % 600) / 1000;
      const baseBid = eng.bid_amount ?? mockBid;
      const mockRevenue = Math.max(
        50_000,
        Math.min(5_000_000, Math.round((baseBid * variance) / 10_000) * 10_000),
      );

      // Retainer monthly value: 8K – 80K, snapped to nearest 500
      const monthlyRange = 80_000 - 8_000;
      const mockMonthly =
        8_000 + Math.round(((hash(seed + ':mo')) % monthlyRange) / 500) * 500;

      // Profit margin: 15% – 45%, one decimal
      const mockMargin = 15 + ((hash(seed + ':margin')) % 3001) / 100;

      return {
        ...eng,
        bid_amount: eng.bid_amount ?? mockBid,
        actual_revenue: eng.actual_revenue ?? (isRetainer ? null : mockRevenue),
        retainer_monthly_value:
          eng.retainer_monthly_value ?? (isRetainer ? mockMonthly : null),
        profit_margin: eng.profit_margin ?? Math.round(mockMargin * 10) / 10,
      };
    });
  }, [wonEngagementsData]);

  // ── UI-side filtering ─────────────────────────────────────────────
  const filteredEngagements = useMemo(() => {
    if (!realEngagements?.length) return [];

    return realEngagements.filter((eng) => {
      if (wizardData.filters?.searchClient) {
        const term  = wizardData.filters.searchClient.toLowerCase();
        const match =
          eng.engagement_name?.toLowerCase().includes(term) ||
          eng.qb_customer_name?.toLowerCase().includes(term) ||
          eng.industry?.toLowerCase().includes(term);
        if (!match) return false;
      }

      if (wizardData.filters?.industry) {
        if (eng.industry !== wizardData.filters.industry) return false;
      }

      if (wizardData.filters?.practiceArea) {
        if (eng.practice_area !== wizardData.filters.practiceArea) return false;
      }

      if (wizardData.filters?.tags?.length) {
        const engTags  = eng.tags || [];
        const allMatch = wizardData.filters.tags.every((t) => engTags.includes(t));
        if (!allMatch) return false;
      }

      if (wizardData.filters?.excludeUnknown) {
        if (!eng.qb_customer_name || eng.qb_customer_name.toLowerCase().includes('unknown')) return false;
      }

      if (wizardData.filters?.timePeriod && wizardData.filters.timePeriod !== 'all') {
        const engDate = eng.start_date ? new Date(eng.start_date) : null;
        if (!engDate) return false;

        const now     = new Date();
        const cutoffs = {
          last30:       new Date(now - 30  * 86400000),
          last90:       new Date(now - 90  * 86400000),
          last180:      new Date(now - 180 * 86400000),
          lastYear:     new Date(now - 365 * 86400000),
          thisYear:     new Date(now.getFullYear(), 0, 1),
          lastTwoYears: new Date(now - 730 * 86400000),
        };

        if (wizardData.filters.timePeriod === 'custom') {
          const start = wizardData.filters.startDate ? new Date(wizardData.filters.startDate) : null;
          const end   = wizardData.filters.endDate   ? new Date(wizardData.filters.endDate)   : null;
          if (start && engDate < start) return false;
          if (end   && engDate > end)   return false;
        } else {
          const cutoff = cutoffs[wizardData.filters.timePeriod];
          if (cutoff && engDate < cutoff) return false;
        }
      }

      return true;
    });
  }, [realEngagements, wizardData.filters]);

  // ── Document handlers ─────────────────────────────────────────────
  const handlePreviewDocument = async (documentId) => {
    try {
      setPreviewDocumentId(documentId);
      setShowPreviewModal(true);
    } catch (err) {
      console.error('Error opening preview:', err);
      alert('Failed to open document preview. Please try again.');
    }
  };

  const handleDownloadDocument = async (documentId, documentName) => {
    try {
      const response = await apiService.get(`/documents/${documentId}/download`);
      if (response?.download_url) {
        const link    = document.createElement('a');
        link.href     = response.download_url;
        link.download = documentName || 'document';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Error downloading:', err);
    }
  };

  // ── Engagement modal handlers ─────────────────────────────────────
  const handleCreateEngagement = () => {
    setEditingEngagement(null);
    setEngagementFormData(
      getEmptyFormData({
        engagementType: wizardData.engagementType === 'retainer' ? 'Retainer' : 'Project',
        industry:       wizardData.filters.industry || '',
        bidStatus:      'Won',
      })
    );
    setShowEngagementModal(true);
  };

  const handleEditEngagement = (engagement) => {
    setEditingEngagement(engagement);
    setEngagementFormData(engagementToFormData(engagement));
    originalDocIdRef.current = engagement.document_id || null;
    setShowEngagementModal(true);
  };

  const handleCloseEngagementModal = () => {
    setShowEngagementModal(false);
    setEditingEngagement(null);
  };

  const handleEngagementSubmit = () => {
    if (!engagementFormData.engagementName) return;

    const isEditing = !!editingEngagement;
    const engId     = editingEngagement?.id;
    const oldDocId  = originalDocIdRef.current;
    const newDocId  = engagementFormData.documentId || null;
    const payload   = formDataToPayload(engagementFormData);

    // Close modal immediately — optimistic update handles the UI
    handleCloseEngagementModal();

    if (isEditing) {
      // Handle document linking side-effect (fire-and-forget)
      if (newDocId !== oldDocId) {
        if (oldDocId) apiService.patch(`/documents/${oldDocId}`, { engagement_id: null }).catch(console.error);
        if (newDocId) apiService.patch(`/documents/${newDocId}`, { engagement_id: engId }).catch(console.error);
      }

      // Show saving indicator on the row
      setSaveStates((prev) => ({ ...prev, [engId]: 'saving' }));

      updateEngagementMutation.mutate(
        { engagementId: engId, updates: payload },
        {
          onSuccess: () => {
            // Flash "saved" checkmark for 2s then clear
            setSaveStates((prev) => ({ ...prev, [engId]: 'saved' }));
            setTimeout(() => {
              setSaveStates((prev) => {
                const next = { ...prev };
                delete next[engId];
                return next;
              });
            }, 2000);
            // NOTE: no clearCache or refetchEngagements here —
            // useUpdateEngagement.onSuccess already writes server data
            // directly into both caches, so no network round-trip needed.
          },
          onError: (err) => {
            console.error('Error updating engagement:', err);
            setSaveStates((prev) => ({ ...prev, [engId]: 'error' }));
            setTimeout(() => {
              setSaveStates((prev) => {
                const next = { ...prev };
                delete next[engId];
                return next;
              });
            }, 4000);
          },
        }
      );
    } else {
      createEngagementMutation.mutate(payload, {
        onSuccess: async (result) => {
          const newId = result?.data?.id || result?.id;
          if (newDocId && newId) {
            await apiService.patch(`/documents/${newDocId}`, { engagement_id: newId }).catch(console.error);
          }
          // invalidateQueries in useCreateEngagement handles the refetch
        },
        onError: (err) => console.error('Error creating engagement:', err),
      });
    }
  };

  const handleSave = () => onSaveRequest?.(wizardData, calculations);

  // ── Load scenario from SavedBidsTab ──────────────────────────────
  const handleLoadScenario = (scenario) => {
    setLoadedScenarioId(scenario.id ?? undefined);
    const hasTemplate = !!scenario.selected_template?.id;
    const hasBaseline = !!scenario.selected_baseline?.id;

    setWizardData((prev) => ({
      ...prev,
      engagementType:   scenario.engagement_type   ?? prev.engagementType,
      clientName:       scenario.client_name       ?? prev.clientName,
      services:         scenario.services          ?? prev.services,
      selectedBaseline: scenario.selected_baseline ?? null,
      selectedTemplate: scenario.selected_template ?? null,
      useReference:     hasTemplate ? true : hasBaseline ? false : prev.useReference,
    }));

    setLoadedMargin(
      scenario.margin !== undefined && scenario.margin !== null
        ? scenario.margin
        : undefined
    );
    setLoadedRateOverrides(
      scenario.rate_overrides && Object.keys(scenario.rate_overrides).length > 0
        ? scenario.rate_overrides
        : undefined
    );
    setLoadedClientName(scenario.client_name ?? undefined);
    setLoadedNotes(scenario.notes ?? undefined);
    setLoadedScenarioName(scenario.name ?? undefined);

    goToStep(4);
  };

  // ── Reset wizard for a new bid ────────────────────────────────────
  const handleNewBid = () => {
    setPreFilteredComponents(null);
    setLoadedMargin(undefined);
    setLoadedRateOverrides(undefined);
    setLoadedClientName(undefined);
    setLoadedNotes(undefined);
    setLoadedScenarioId(undefined);
    setLoadedScenarioName(undefined);
    autoSyncAttemptedRef.current = null;
    setAutoSyncing(false);
    setAutoSyncError(null);
    goToStep(1);
  };

  // ── Bid engagements hook ──────────────────────────────────────────
  const {
    data:      bidEngagements,
    isLoading: isLoadingBidEngagements,
  } = useBidEngagements({
    engagementType: wizardData.engagementType,
    isActive:       true,
    limit:          50,
    enabled:        currentStep >= 3,
  });

  // ── Render step content ───────────────────────────────────────────
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1EngagementType
            wizardData={wizardData}
            onSelectEngagementType={(type, id, name, projects, retainerServices) => {
              selectEngagementType(type, id, name, projects, retainerServices);
            }}
          />
        );

      case 2:
        return (
          <Step2ReferenceDecision
            pricingContext={pricingContext}
            wizardData={wizardData}
            isLoadingEngagements={isLoadingEngagements}
            realEngagementsCount={realEngagements.length}
            onSelectReferenceDecision={selectReferenceDecision}
            onNextStep={nextStep}
          />
        );

      case 3:
        if (wizardData.useReference === true) {
          return (
            <Step3SelectTemplate
              wizardData={wizardData}
              filteredEngagements={filteredEngagements}
              realEngagements={realEngagements}
              isLoadingEngagements={isLoadingEngagements}
              onUpdateFilter={updateFilter}
              onRefetchEngagements={refetchEngagements}
              onCreateEngagement={handleCreateEngagement}
              onEditEngagement={handleEditEngagement}
              onSelectTemplate={(engagement, filteredComponents) => {
                selectEngagementTemplate(engagement);
                if (filteredComponents?.length) {
                  setPreFilteredComponents(filteredComponents);
                }
              }}
              onPreviewDocument={handlePreviewDocument}
              onDownloadDocument={handleDownloadDocument}
              onNextStep={nextStep}
              saveStates={saveStates}
            />
          );
        }

        if (wizardData.useReference === false) {
          return (
            <Step3BaselineSelection
              onSelectBaseline={(baseline) => {
                const services = buildServicesFromBaseline(baseline);
                setWizardData((prev) => ({
                  ...prev,
                  selectedBaseline: baseline,
                  services,
                }));
              }}
              onNextStep={nextStep}
              onPrevStep={previousStep}
            />
          );
        }

        return null;

      case 4:
        return (
          <Step4BuildServices
            wizardData={wizardData}
            setWizardData={setWizardData}
            calculations={calculations}
            insights={insights}
            pricingContext={pricingContext}
            serviceManagement={serviceManagement}
            componentsLoading={componentsLoading}
            hasComponentsData={!!templateComponents?.components?.length}
            selectedTemplate={referenceDecision === true  ? wizardData.selectedTemplate : null}
            selectedBaseline={referenceDecision === false ? wizardData.selectedBaseline : null}
            autoSyncing={autoSyncing}
            autoSyncError={autoSyncError}
            initialScenarioId={loadedScenarioId}
            initialScenarioName={loadedScenarioName}
            initialMargin={loadedMargin}
            initialRateOverrides={loadedRateOverrides}
            initialClientName={loadedClientName}
            initialNotes={loadedNotes}
          />
        );

      case 5:
        return (
          <Step5GenerateProposal
            wizardData={wizardData}
            calculations={calculations}
          />
        );

      default:
        return null;
    }
  };

  // ── Main render ───────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <WizardStepper
        currentStep={currentStep}
        referenceDecision={referenceDecision}
        onGoToStep={goToStep}
      />

      <div className="bg-white rounded-lg border border-gray-200 p-6 sm:p-8">
        {renderStepContent()}

        <WizardNavigation
          currentStep={currentStep}
          displayStep={displayStep}
          totalSteps={totalSteps}
          canProceed={() => {
            if (currentStep === 1) {
              return wizardData.componentType || (wizardData.selectedProjects?.length > 0);
            }
            return canProceed();
          }}
          onPreviousStep={previousStep}
          onNextStep={() => nextStep()}
          onSave={handleSave}
          isSaving={isSaving}
        />
      </div>

      <EngagementModal
        isOpen={showEngagementModal}
        isEdit={!!editingEngagement}
        existingEngagement={editingEngagement}
        formData={engagementFormData}
        setFormData={setEngagementFormData}
        onSubmit={handleEngagementSubmit}
        onClose={handleCloseEngagementModal}
        isLoading={false}
        documents={documents}
        loadingDocuments={loadingDocuments}
        onPreviewDocument={handlePreviewDocument}
        onDownloadDocument={handleDownloadDocument}
        industries={Object.keys(INDUSTRIES)}
      />

      {showPreviewModal && previewDocumentId && (
        <PreviewModal
          documentId={previewDocumentId}
          documents={documents}
          onClose={() => {
            setShowPreviewModal(false);
            setPreviewDocumentId(null);
          }}
        />
      )}
    </div>
  );
};

export default BidWizard;